import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { Eye, EyeOff, Lock, KeyRound, MonitorSmartphone } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from './ui/alert-dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function formatApiErrorDetail(detail) {
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(' ');
  return null;
}

/**
 * SecuritySection — In-app change-password panel.
 *
 * Behaves differently based on how the user signed up:
 *   - auth_provider = 'email' or 'both': full 3-field change form
 *   - auth_provider = 'google' (no password): shows an info card + a
 *     "Set up password" CTA that reuses the existing forgot-password flow
 *     for verified email ownership. Never assigns a password directly.
 */
export const SecuritySection = ({ user, onPasswordSetupRequested }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const canChange = Boolean(user && (user.auth_provider === 'email' || user.auth_provider === 'both'));

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [signingOutAll, setSigningOutAll] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const validate = () => {
    if (!current) return t('auth.errors.currentRequired');
    if (next !== confirm) return t('auth.errors.passwordMismatch');
    if (next.length < 8) return t('auth.errors.passwordShort');
    if (!/[A-Za-z]/.test(next) || !/\d/.test(next)) return t('auth.errors.passwordWeak');
    if (next === current) return t('auth.errors.passwordSame');
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) { toast.error(err); return; }
    if (submitting) return;
    setSubmitting(true);
    try {
      const resp = await axios.post(
        `${BACKEND_URL}/api/auth/change-password`,
        { current_password: current, new_password: next },
        { withCredentials: true },
      );
      toast.success(resp.data?.message || t('auth.security.changed'));
      setCurrent(''); setNext(''); setConfirm('');
    } catch (err2) {
      toast.error(formatApiErrorDetail(err2.response?.data?.detail) || t('auth.errors.generic'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleSetupPassword = async () => {
    if (!user?.email) return;
    try {
      await axios.post(
        `${BACKEND_URL}/api/auth/forgot-password`,
        { email: user.email },
        { withCredentials: true },
      );
      toast.success(t('auth.security.setupSent'));
      if (onPasswordSetupRequested) onPasswordSetupRequested();
    } catch {
      toast.success(t('auth.security.setupSent'));
    }
  };

  const handleSignOutEverywhere = async () => {
    if (signingOutAll) return;
    setSigningOutAll(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/auth/logout-all-sessions`,
        {},
        { withCredentials: true },
      );
      toast.success(t('auth.sessions.success'));
      setConfirmOpen(false);
      // The backend already cleared the session cookie. Redirect to login.
      navigate('/login', { replace: true });
    } catch (err) {
      const status = err.response?.status;
      if (status === 429) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || t('auth.errors.generic'));
      } else if (status === 401) {
        // Already signed out — go to login.
        navigate('/login', { replace: true });
      } else {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || t('auth.errors.generic'));
      }
    } finally {
      setSigningOutAll(false);
    }
  };

  return (
    <div className="space-y-6">
      <div
        data-testid="security-section"
        className="bg-white dark:bg-[#131824] border border-slate-200 dark:border-slate-800 rounded-2xl p-6 md:p-8"
      >
      <div className="flex items-center gap-3 mb-1">
        <div className="w-10 h-10 rounded-xl bg-[#45C04A]/10 flex items-center justify-center">
          <Lock className="w-5 h-5 text-[#45C04A]" />
        </div>
        <h2 data-testid="security-title" className="font-primary font-bold text-xl md:text-2xl text-[#12355B] dark:text-white">
          {t('auth.security.title')}
        </h2>
      </div>
      <p className="text-sm text-slate-500 dark:text-slate-400 ml-13 mb-6">{t('auth.security.subtitle')}</p>

      {!canChange ? (
        <div data-testid="security-google-only" className="rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 p-5">
          <p className="text-sm text-slate-700 dark:text-slate-200 mb-4">{t('auth.security.googleOnly')}</p>
          <Button
            data-testid="security-setup-password-btn"
            type="button"
            onClick={handleSetupPassword}
            className="rounded-xl bg-[#12355B] hover:bg-[#12355B]/90 dark:bg-[#45C04A] dark:hover:bg-[#65D95A] dark:text-[#12355B] text-white"
          >
            <KeyRound className="w-4 h-4 mr-2" />
            {t('auth.security.setupBtn')}
          </Button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4 max-w-md">
          <div>
            <Label htmlFor="current_password">{t('auth.security.currentPassword')}</Label>
            <div className="relative">
              <Input
                id="current_password"
                data-testid="security-current-password"
                type={showCurrent ? 'text' : 'password'}
                autoComplete="current-password"
                required
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                className="mt-1 pr-10"
              />
              <button
                type="button"
                aria-label={showCurrent ? t('auth.security.hide') : t('auth.security.show')}
                onClick={() => setShowCurrent(!showCurrent)}
                className="absolute right-3 top-3.5 text-slate-500 hover:text-slate-700 dark:hover:text-white"
              >
                {showCurrent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div>
            <Label htmlFor="new_password">{t('auth.security.newPassword')}</Label>
            <div className="relative">
              <Input
                id="new_password"
                data-testid="security-new-password"
                type={showNext ? 'text' : 'password'}
                autoComplete="new-password"
                required
                value={next}
                onChange={(e) => setNext(e.target.value)}
                className="mt-1 pr-10"
              />
              <button
                type="button"
                aria-label={showNext ? t('auth.security.hide') : t('auth.security.show')}
                onClick={() => setShowNext(!showNext)}
                className="absolute right-3 top-3.5 text-slate-500 hover:text-slate-700 dark:hover:text-white"
              >
                {showNext ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <p className="mt-1 text-xs text-slate-500">{t('auth.signup.passwordHint')}</p>
          </div>
          <div>
            <Label htmlFor="confirm_password">{t('auth.security.confirmPassword')}</Label>
            <Input
              id="confirm_password"
              data-testid="security-confirm-password"
              type={showNext ? 'text' : 'password'}
              autoComplete="new-password"
              required
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="mt-1"
            />
          </div>
          <Button
            data-testid="security-change-password-btn"
            type="submit"
            disabled={submitting}
            className="rounded-xl bg-[#12355B] hover:bg-[#12355B]/90 dark:bg-[#45C04A] dark:hover:bg-[#65D95A] dark:text-[#12355B] text-white"
          >
            {submitting ? t('auth.security.submitting') : t('auth.security.submit')}
          </Button>
        </form>
      )}
      </div>

      {/* Active Sessions — Sign Out Everywhere */}
      <div
        data-testid="sessions-section"
        className="bg-white dark:bg-[#131824] border border-slate-200 dark:border-slate-800 rounded-2xl p-6 md:p-8"
      >
        <div className="flex items-center gap-3 mb-1">
          <div className="w-10 h-10 rounded-xl bg-[#45C04A]/10 flex items-center justify-center">
            <MonitorSmartphone className="w-5 h-5 text-[#45C04A]" />
          </div>
          <h2
            data-testid="sessions-title"
            className="font-primary font-bold text-xl md:text-2xl text-[#12355B] dark:text-white"
          >
            {t('auth.sessions.title')}
          </h2>
        </div>
        <p className="text-sm text-slate-500 dark:text-slate-400 ml-13 mb-6">
          {t('auth.sessions.description')}
        </p>
        <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
          <AlertDialogTrigger asChild>
            <Button
              data-testid="sessions-signout-all-btn"
              type="button"
              variant="outline"
              className="rounded-xl border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700 dark:border-red-700/60 dark:text-red-400 dark:hover:bg-red-950/40"
            >
              <MonitorSmartphone className="w-4 h-4 mr-2" />
              {t('auth.sessions.signOutAll')}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent data-testid="sessions-signout-all-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle data-testid="sessions-dialog-title">
                {t('auth.sessions.confirmTitle')}
              </AlertDialogTitle>
              <AlertDialogDescription data-testid="sessions-dialog-desc">
                {t('auth.sessions.confirmBody')}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel
                data-testid="sessions-dialog-cancel"
                disabled={signingOutAll}
              >
                {t('auth.sessions.cancel')}
              </AlertDialogCancel>
              <AlertDialogAction
                data-testid="sessions-dialog-confirm"
                onClick={(e) => {
                  e.preventDefault();
                  handleSignOutEverywhere();
                }}
                disabled={signingOutAll}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                {signingOutAll ? t('auth.sessions.submitting') : t('auth.sessions.signOutAll')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
};

export default SecuritySection;
